import type { UseFormReturn } from 'react-hook-form';
import { Users, Music } from 'lucide-react';
import { usePeerGroupsForDropdown } from '../../../../hooks/usePeerGroups';
import { useCodecGroupsForDropdown } from '../../../../hooks/useCodecGroups';
import type { SipPeerFormData } from '../schema';

/** Groups tab: peer group membership + codec group selection. */
export function GroupsTab({ form }: { form: UseFormReturn<SipPeerFormData> }) {
  const { data: peerGroups, isLoading: peerGroupsLoading } = usePeerGroupsForDropdown();
  const { data: codecGroups, isLoading: codecGroupsLoading } = useCodecGroupsForDropdown();
  const groupId = form.watch('groupId');

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Groups</h3>
        <p className="text-sm text-gray-600 mb-4">
          Optionally assign this peer to a peer group and select a codec group.
        </p>
      </div>

      {/* Peer Group Membership */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <h4 className="font-medium text-gray-900 flex items-center">
          <Users className="w-5 h-5 mr-2 text-purple-600" />
          Peer Group Membership
        </h4>
        <p className="text-xs text-gray-500 mb-2">
          Optionally assign this peer to a peer group for load balancing and redundancy.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Peer Group
            </label>
            <select
              {...form.register('groupId')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={peerGroupsLoading}
            >
              <option value="">No peer group (standalone peer)</option>
              {peerGroups?.map((group) => (
                <option key={group.value} value={group.value}>
                  {group.label} {group.groupType && `(${group.groupType})`}
                </option>
              ))}
            </select>
            {peerGroupsLoading && (
              <p className="text-xs text-gray-500 mt-1">Loading peer groups...</p>
            )}
          </div>

          {/* Group membership settings - only show when a group is selected */}
          {groupId && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Group Weight (0-100)
                </label>
                <input
                  {...form.register('groupWeight', { valueAsNumber: true })}
                  type="number"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Load balancing weight within the group</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Group Priority (1-100)
                </label>
                <input
                  {...form.register('groupPriority', { valueAsNumber: true })}
                  type="number"
                  min="1"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Failover priority within the group</p>
              </div>

              <div className="flex items-center md:col-span-2">
                <input
                  {...form.register('groupEnabled')}
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Active in this group
                </label>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Codec Group Selection */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900 flex items-center">
            <Music className="w-5 h-5 mr-2 text-blue-600" />
            Codec Configuration
          </h4>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          Select a codec group or leave empty to use the default system codecs.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Codec Group
          </label>
          <select
            {...form.register('codecGroupId')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={codecGroupsLoading}
          >
            <option value="">Use default codecs</option>
            {codecGroups?.map((group) => (
              <option key={group.value} value={group.value}>
                {group.label} {group.isDefault && '- Default'}
              </option>
            ))}
          </select>
          {codecGroupsLoading && (
            <p className="text-xs text-gray-500 mt-1">Loading codec groups...</p>
          )}
        </div>
      </div>
    </div>
  );
}
